import { useState, useEffect } from 'react'
import {
  Sliders,
  CheckCircle2,
  Save,
  FileCheck,
  Percent,
} from 'lucide-react'
import { Button, Card, CardContent, Input } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { useAdminSettingsQuery, useUpdateSettingsMutation } from '../../hooks/use-admin'

export function WorkflowSettingsPage() {
  const { data: rawSettings } = useAdminSettingsQuery()
  const updateSettingsMutation = useUpdateSettingsMutation()

  // Listing Workflow Configuration State
  const [listingWorkflowMode, setListingWorkflowMode] = useState<'AUTO_PUBLISH' | 'ADMIN_REVIEW'>('AUTO_PUBLISH')
  
  // Profile Completion Requirements
  const [requireGst, setRequireGst] = useState(true)
  const [requirePan, setRequirePan] = useState(true)
  const [requireAadhaar, setRequireAadhaar] = useState(true)
  const [requireBankDetails, setRequireBankDetails] = useState(true)
  const [requireAddress, setRequireAddress] = useState(true)
  const [requirePhoto, setRequirePhoto] = useState(false)
  const [requireLogo, setRequireLogo] = useState(false)
  const [minProfilePercent, setMinProfilePercent] = useState(80)

  useEffect(() => {
    if (rawSettings && Array.isArray(rawSettings)) {
      const workflowSetting = rawSettings.find((s: any) => s.key === 'LISTING_WORKFLOW_MODE')
      if (workflowSetting) setListingWorkflowMode(workflowSetting.value)

      const profileRules = rawSettings.find((s: any) => s.key === 'PROFILE_COMPLETION_RULES')
      if (profileRules && profileRules.value) {
        const val = profileRules.value
        setRequireGst(val.requireGst ?? true)
        setRequirePan(val.requirePan ?? true)
        setRequireAadhaar(val.requireAadhaar ?? true)
        setRequireBankDetails(val.requireBankDetails ?? true)
        setRequireAddress(val.requireAddress ?? true)
        setRequirePhoto(val.requirePhoto ?? false)
        setRequireLogo(val.requireLogo ?? false)
        setMinProfilePercent(val.minProfilePercent ?? 80)
      }
    }
  }, [rawSettings])

  const handleSaveSettings = async () => {
    await updateSettingsMutation.mutateAsync([
      { key: 'LISTING_WORKFLOW_MODE', value: listingWorkflowMode },
      {
        key: 'PROFILE_COMPLETION_RULES',
        value: {
          requireGst,
          requirePan,
          requireAadhaar,
          requireBankDetails,
          requireAddress,
          requirePhoto,
          requireLogo,
          minProfilePercent,
        },
      },
    ])
  }

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeading
          eyebrow="Platform Policy & Governance"
          title="Listing Workflows & Profile Rules"
          description="Configure car and part listing publishing lifecycles and merchant profile completion prerequisites."
          align="left"
        />
        <Button onClick={handleSaveSettings} disabled={updateSettingsMutation.isPending} className="gap-2 font-bold shrink-0 self-start sm:self-auto">
          <Save className="size-4" /> Save System Settings
        </Button>
      </div>

      {/* Listing Publishing Lifecycle Workflow */}
      <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 border-b border-border/60 pb-3">
            <Sliders className="size-5 text-primary" />
            <h3 className="font-display text-base font-bold text-foreground">
              Vehicle & Parts Listing Publishing Workflow
            </h3>
          </div>

          <p className="text-xs text-muted-foreground">
            Decide whether newly created vehicle and spare part listings are automatically published or require admin moderation before going live in the marketplace.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <div
              onClick={() => setListingWorkflowMode('AUTO_PUBLISH')}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${
                listingWorkflowMode === 'AUTO_PUBLISH'
                  ? 'border-primary bg-primary/10 shadow-elevation-xs'
                  : 'border-border/80 bg-background/50 hover:bg-background/80'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-bold text-sm text-foreground">Auto-Publish Mode</span>
                {listingWorkflowMode === 'AUTO_PUBLISH' && <CheckCircle2 className="size-4 text-primary" />}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Draft → Submit → <span className="font-bold text-emerald-400">Published</span> → Boosted → Sold → Archived
              </p>
            </div>

            <div
              onClick={() => setListingWorkflowMode('ADMIN_REVIEW')}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${
                listingWorkflowMode === 'ADMIN_REVIEW'
                  ? 'border-purple-500 bg-purple-500/10 shadow-elevation-xs'
                  : 'border-border/80 bg-background/50 hover:bg-background/80'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-bold text-sm text-foreground">Admin Moderation Queue</span>
                {listingWorkflowMode === 'ADMIN_REVIEW' && <CheckCircle2 className="size-4 text-purple-400" />}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Draft → Submit → <span className="font-bold text-amber-400">Admin Review</span> → Published → Sold
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Completion Prerequisites */}
      <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-2 border-b border-border/60 pb-3">
            <FileCheck className="size-5 text-primary" />
            <h3 className="font-display text-base font-bold text-foreground">
              Profile Completion Prerequisites
            </h3>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 text-xs">
            <label className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border/60 bg-background/50 cursor-pointer">
              <span>Require GST Identification Number</span>
              <input
                type="checkbox"
                checked={requireGst}
                onChange={(e) => setRequireGst(e.target.checked)}
                className="rounded border-border text-primary"
              />
            </label>

            <label className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border/60 bg-background/50 cursor-pointer">
              <span>Require PAN Identification</span>
              <input
                type="checkbox"
                checked={requirePan}
                onChange={(e) => setRequirePan(e.target.checked)}
                className="rounded border-border text-primary"
              />
            </label>

            <label className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border/60 bg-background/50 cursor-pointer">
              <span>Require Aadhaar Identity Proof</span>
              <input
                type="checkbox"
                checked={requireAadhaar}
                onChange={(e) => setRequireAadhaar(e.target.checked)}
                className="rounded border-border text-primary"
              />
            </label>

            <label className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border/60 bg-background/50 cursor-pointer">
              <span>Require Bank Account Details</span>
              <input
                type="checkbox"
                checked={requireBankDetails}
                onChange={(e) => setRequireBankDetails(e.target.checked)}
                className="rounded border-border text-primary"
              />
            </label>

            <label className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border/60 bg-background/50 cursor-pointer">
              <span>Require Physical Address Proof</span>
              <input
                type="checkbox"
                checked={requireAddress}
                onChange={(e) => setRequireAddress(e.target.checked)}
                className="rounded border-border text-primary"
              />
            </label>

            <label className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border/60 bg-background/50 cursor-pointer">
              <span>Require Business Logo Upload</span>
              <input
                type="checkbox"
                checked={requireLogo}
                onChange={(e) => setRequireLogo(e.target.checked)}
                className="rounded border-border text-primary"
              />
            </label>
          </div>

          <div className="space-y-1.5 pt-2 border-t border-border/60">
            <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
              <Percent className="size-3.5 text-primary" /> Minimum Profile Completion Threshold (%):
            </label>
            <Input
              type="number"
              value={minProfilePercent}
              onChange={(e) => setMinProfilePercent(Number(e.target.value))}
              min={0}
              max={100}
              className="max-w-xs text-xs"
            />
            <p className="text-[11px] text-muted-foreground">
              Merchant listing creation rights will remain locked until the user satisfies this profile completion percentage.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
